// Standalone tools registry for Agent tool-calling
// Uses website tool schemas for compatibility, but implements handlers here.
// Only dependency: ToolContext type (service access via context).

import { type ToolContext } from './ToolContext';

// Import existing tool schemas to keep API consistent with website
// Schemas copied locally to remove dependency on website/lib/tools/*
const listFilesToolSchema = {
  name: 'listFiles',
  description: 'List all files in the project',
  input_schema: { type: 'object' as const, properties: {}, required: [] }
};

const readFileToolSchema = {
  name: 'readFile',
  description: 'Read the contents of a specific file, optionally reading only a portion of it',
  input_schema: {
    type: 'object' as const,
    properties: {
      path: { type: 'string', description: 'The path to the file to read' },
      startLine: { type: 'number', description: 'Optional starting line number (1-indexed)' },
      lineCount: { type: 'number', description: 'Optional number of lines to read from startLine' }
    },
    required: ['path']
  }
};

const createFileToolSchema = {
  name: 'createFile',
  description: 'Create a new file with specified content',
  input_schema: {
    type: 'object' as const,
    properties: {
      path: { type: 'string', description: 'The path where the file should be created' },
      content: { type: 'string', description: 'The content to write to the file' }
    },
    required: ['path', 'content']
  }
};

const writeFileToolSchema = {
  name: 'writeFile',
  description: 'Write complete content to a file (creates or overwrites)',
  input_schema: {
    type: 'object' as const,
    properties: {
      path: { type: 'string', description: 'The path to the file to write' },
      content: { type: 'string', description: 'The complete content to write to the file' }
    },
    required: ['path', 'content']
  }
};

const editFileToolSchema = {
  name: 'editFile',
  description: 'Perform a single targeted text replacement in a file',
  input_schema: {
    type: 'object' as const,
    properties: {
      path: { type: 'string', description: 'The path to the file to edit' },
      oldString: { type: 'string', description: 'Exact text to replace (must match only once)' },
      newString: { type: 'string', description: 'Replacement text' }
    },
    required: ['path', 'oldString', 'newString']
  }
};

const copyFileToolSchema = {
  name: 'copyFile',
  description: 'Copy a file from one location to another',
  input_schema: {
    type: 'object' as const,
    properties: {
      sourcePath: { type: 'string', description: 'The path to the source file' },
      destinationPath: { type: 'string', description: 'The path where the file should be copied' },
      overwrite: { type: 'boolean', description: 'Whether to overwrite the destination if it exists', default: false }
    },
    required: ['sourcePath', 'destinationPath']
  }
};

const rgSearchToolSchema = {
  name: 'rgSearch',
  description: 'Regex-based search over the file set. Supports JS-style regular expressions with inline flags and glob filters.',
  input_schema: {
    type: 'object' as const,
    properties: {
      pattern: {
        type: 'string',
        description: 'Required JS RegExp pattern. Use inline flags like (?i) for case-insensitive or (?s) for dotAll.'
      },
      globs: {
        type: 'array',
        items: { type: 'string' },
        description: 'Optional glob filters (order matters). Include/exclude patterns such as ["src/**/*.ts", "!src/**/__tests__/**"].'
      },
    },
    required: ['pattern']
   },
  // notes: [
  //   'Matches are reported with path, line, column, matched text, and source line.',
  //   'Pattern syntax is JavaScript RegExp (no PCRE-only features).',
  //   'Use (?m) for multiline anchors and (?s) for cross-line matches instead of CLI flags.',
  //   'Glob handling resembles ripgrep’s -g semantics, but brace expansion and advanced patterns are not supported.'
  // ]
};

const writeTodosToolSchema = {
  name: 'writeTodos',
  description: 'Create and manage a structured task list for the current coding session. Use this tool to track progress, organize complex tasks, and demonstrate thoroughness to the user.',
  input_schema: {
    type: 'object' as const,
    properties: {
      todos: {
        type: 'array', description: 'Array of todo items to create or update',
        items: {
          type: 'object',
          properties: {
            content: { type: 'string', description: 'Brief description of the task' },
            status: { type: 'string', enum: ['pending', 'in_progress', 'completed', 'cancelled'], description: 'Current status of the task' },
            priority: { type: 'string', enum: ['high', 'medium', 'low'], description: 'Priority level of the task' }
          },
          required: ['content', 'status', 'priority']
        }
      }
    },
    required: ['todos']
  }
};

const readTodosToolSchema = {
  name: 'readTodos',
  description: 'Read the current todo list for the session. Use this tool frequently to track progress and understand current tasks.',
  input_schema: { type: 'object' as const, properties: {}, required: [] }
};

const updateTodoStatusToolSchema = {
  name: 'updateTodoStatus',
  description: 'Update the status of a specific todo item (mark as in_progress, completed, etc.)',
  input_schema: {
    type: 'object' as const,
    properties: {
      todoId: { type: 'number', description: 'The ID of the todo item to update' },
      status: { type: 'string', enum: ['pending', 'in_progress', 'completed', 'cancelled'], description: 'New status for the todo item' }
    },
    required: ['todoId', 'status']
  }
};

const libraryListToolSchema = {
  name: 'libraryList',
  description: 'List all available libraries that can be installed into the app',
  input_schema: { type: 'object' as const, properties: {}, required: [] }
};

const libraryDocsToolSchema = {
  name: 'libraryDocs',
  description: 'Get documentation for a specific library including setup instructions and API reference',
  input_schema: {
    type: 'object' as const,
    properties: { libName: { type: 'string', description: 'The name of the library to get documentation for' } },
    required: ['libName']
  }
};

const diffToolSchema = {
  name: 'diff',
  description: 'Produce a unified diff between the active filesystem and stored versions to assist with merges',
  input_schema: { type: 'object' as const, properties: {}, required: [] }
};

// Keep local copy of ToolCall shape to avoid circular import with Agent
export interface ToolCall {
  id: string;
  function: { name: string; arguments?: Record<string, any> };
}

// Tool interface
interface ToolDef {
  name: string;
  schema: any;
  handler: (ctx: ToolContext, args: Record<string, any>) => Promise<string>;
}

// Handlers (standalone, using only ToolContext services)
async function handleListFiles(ctx: ToolContext): Promise<string> {
  const files = await ctx.fileService.list();
  return JSON.stringify({ files, count: files.length });
}

async function handleReadFile(ctx: ToolContext, args: any): Promise<string> {
  const { path, startLine, lineCount } = args || {};
  if (!path) throw new Error('readFile requires path');
  const content = await ctx.fileService.read(path);
  if (content === null) throw new Error(`File not found: ${path}`);
  let finalContent = content;
  if (startLine !== undefined) {
    const lines = content.split('\n');
    const start = Math.max(0, Number(startLine) - 1);
    const end = lineCount !== undefined ? Math.min(lines.length, start + Number(lineCount)) : lines.length;
    finalContent = lines.slice(start, end).join('\n');
  }
  return JSON.stringify({
    path,
    content: finalContent,
    size: finalContent.length,
    ...(startLine !== undefined && { startLine, totalLines: (content.match(/\n/g)?.length ?? 0) + 1 }),
    ...(lineCount !== undefined && { lineCount })
  });
}

async function handleCreateFile(ctx: ToolContext, args: any): Promise<string> {
  const { path, content = '' } = args || {};
  if (!path) throw new Error('createFile requires path');
  const normalized = String(content);
  await ctx.fileService.create(path, normalized);
  return JSON.stringify({ path, created: true, size: normalized.length });
}

async function handleWriteFile(ctx: ToolContext, args: any): Promise<string> {
  const { path, content = '' } = args || {};
  if (!path) throw new Error('writeFile requires path');
  const normalized = String(content);
  await ctx.fileService.create(path, normalized);
  return JSON.stringify({ path, written: true, size: normalized.length });
}

// Single-edit helper delegating to FileService
async function handleEditFile(ctx: ToolContext, args: any): Promise<string> {
  const { path, oldString, newString } = args || {};
  if (!path) throw new Error('editFile requires path');

  if (oldString === undefined || newString === undefined) {
    return JSON.stringify({
      path,
      error: 'editFile requires oldString and newString',
      edited: false,
    })
  }

  const normalizedOld = String(oldString);
  const normalizedNew = String(newString);

  const result = await ctx.fileService.edit(path, normalizedOld, normalizedNew);

  return JSON.stringify({
    path: result.path,
    edited: true,
  });
}

async function handleCopyFile(ctx: ToolContext, args: any): Promise<string> {
  const { sourcePath, destinationPath, overwrite = false } = args || {};
  if (!sourcePath || !destinationPath) throw new Error('copyFile requires sourcePath and destinationPath');
  const sourceContent = await ctx.fileService.read(sourcePath);
  if (sourceContent === null) throw new Error(`Source file not found: ${sourcePath}`);
  const destExists = (await ctx.fileService.read(destinationPath)) !== null;
  if (destExists && !overwrite) throw new Error(`Destination file already exists: ${destinationPath}. Use overwrite flag to replace.`);
  await ctx.fileService.create(destinationPath, sourceContent);
  return JSON.stringify({ sourcePath, destinationPath, copied: true, overwritten: destExists, size: sourceContent.length });
}

export function quickRerank(results: any[], pattern: string, topK: number = 15): any[] {
  if (!results || results.length === 0) {
    return [];
  }

  // Extract search terms from pattern (remove regex special chars for term matching)
  const searchTerms = pattern
    .replace(/[()[\]{}.*+?^$|\\]/g, ' ')
    .split(/\s+/)
    .filter(term => term.length > 2);

  // Count term frequencies
  const counts: Record<string, number> = {};
  results.forEach(r => {
    const term = (r.match ?? '').toLowerCase();
    if (term) {
      counts[term] = (counts[term] || 0) + 1;
    }
  });

  const maxCount = Math.max(...Object.values(counts), 1);

  // Score and sort results
  const scored = results.map(r => {
    const term = (r.match ?? '').toLowerCase();
    const rarityScore = counts[term] ? maxCount / counts[term] : 1;

    // Count how many search terms appear in the full line text
    const textLower = (r.text ?? '').toLowerCase();
    const multiTermScore = searchTerms.filter(t =>
      textLower.includes(t.toLowerCase())
    ).length;

    return {
      ...r,
      score: rarityScore + (multiTermScore * 2)
    };
  });

  return scored
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, topK);
}

function formatSearchResultsForLLMLocal(results: any[], stats: any): string {
  if (!Array.isArray(results) || results.length === 0) {
    return 'No search results found.';
  }
  const sections: string[] = [];

  // Add summary stats
  if (stats) {
    const uniqueFiles = new Set(results.map((r: any) => r?.path)).size;
    const total = stats.total_results ?? results.length;
    const files = stats.files_searched ?? uniqueFiles;
    const showing = results.length;
    sections.push(`Found ${total} matches in ${files} files (showing top ${showing})\n`);
  }

  // Group results by file
  const byFile = new Map<string, any[]>();
  for (const r of results) {
    const arr = byFile.get(r.path) || [];
    arr.push(r);
    byFile.set(r.path, arr);
  }

  // Format each file's results
  for (const [file, items] of byFile) {
    sections.push(`## ${file}`);
    for (const r of items) {
      const lineNum = r.line ?? '?';
      const colNum = r.column ?? '?';
      const matchText = r.match ?? '';
      const fullLine = r.text ?? '';

      sections.push(`Line ${lineNum}:${colNum} - Match: "${matchText}"`);
      sections.push(`\`${fullLine}\``);
      sections.push('');
    }
  }

  return sections.join('\n');
}

export async function handleRgSearch(ctx: ToolContext, args: any): Promise<string> {
  const { pattern, globs = [], limit } = args || {};
  if (!pattern) throw new Error('rgSearch requires pattern');

  const normalizedPattern = String(pattern);
  const normalizedGlobs = Array.isArray(globs)
    ? globs.map(glob => String(glob)).filter(Boolean)
    : [];

  // Get all matches from FileService
  const matches = await ctx.fileService.rg(normalizedPattern, normalizedGlobs);

  // Determine limit: use provided limit or default to 15
  const topK = limit !== undefined ? Math.max(1, Number(limit)) : 15;

  // Rerank and limit results
  const reranked = quickRerank(matches, normalizedPattern, topK);

  const stats = {
    total_results: matches.length,
    files_searched: new Set(matches.map(match => match.path)).size,
  };

  return formatSearchResultsForLLMLocal(reranked, stats);
}

async function handleWriteTodos(ctx: ToolContext, args: any): Promise<string> {
  const { todos } = args || {};
  if (!todos) throw new Error('writeTodos requires todos');
  await ctx.todoService.clearAllTodos();
  for (const t of todos) await ctx.todoService.createTodo(t.content, t.priority);
  ctx.bus.emit('todo/update');
  return JSON.stringify({ action: 'todos_updated', count: todos.length });
}

async function handleReadTodos(ctx: ToolContext): Promise<string> {
  const todos = await ctx.todoService.getTodos();
  return JSON.stringify({ action: 'todos_read', count: todos.length, todos: todos.map((t: any) => ({ todo_id: t.todo_id, content: t.content, status: t.status, priority: t.priority, created_at: t.created_at, updated_at: t.updated_at })) });
}

async function handleUpdateTodoStatus(ctx: ToolContext, args: any): Promise<string> {
  const { todoId, status } = args || {};
  if (!todoId || !status) throw new Error('updateTodoStatus requires todoId and status');
  await ctx.todoService.updateTodoStatus(Number(todoId), String(status));
  ctx.bus.emit('todo/update');
  return JSON.stringify({ action: 'todo_status_updated', todo_id: Number(todoId), status: String(status) });
}

async function handleLibraryList(ctx: ToolContext): Promise<string> {
  const libraries = await ctx.libraryService.listLibraries();
  return JSON.stringify({ success: true, libraries, count: libraries.length, message: `Found ${libraries.length} available libraries` });
}

async function handleLibraryDocs(ctx: ToolContext, args: any): Promise<string> {
  const { libName } = args || {};
  if (!libName) throw new Error('libraryDocs requires libName');
  const docs = await ctx.libraryService.getLibraryDocs(libName, ctx.env.ASSETS);
  return JSON.stringify({ success: true, library: libName, documentation: docs, message: `Retrieved documentation for library ${libName}` });
}

async function handleDiff(ctx: ToolContext, args: any): Promise<string> {
  const payload = (args ?? {}) as Record<string, any>;

  const coerce = (value: any): number | undefined => {
    if (value === null || value === undefined) {
      return undefined;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      throw new Error(`diff requires numeric identifiers. Received: ${value}`);
    }
    return numeric;
  };

  if ('versionIdA' in payload || 'versionIdB' in payload) {
    const versionIdA = coerce(payload.versionIdA);
    const versionIdB = coerce(payload.versionIdB);
    return ctx.fileService.diff(versionIdA, versionIdB);
  }

  if ('versionId' in payload) {
    const versionId = coerce(payload.versionId);
    return ctx.fileService.diff(versionId);
  }

  return ctx.fileService.diff();
}

// Registry setup
const TOOL_DEFS: ToolDef[] = [
  { name: 'listFiles', schema: listFilesToolSchema, handler: (ctx) => handleListFiles(ctx) },
  { name: 'readFile', schema: readFileToolSchema, handler: (ctx, args) => handleReadFile(ctx, args) },
  { name: 'createFile', schema: createFileToolSchema, handler: (ctx, args) => handleCreateFile(ctx, args) },
  { name: 'writeFile', schema: writeFileToolSchema, handler: (ctx, args) => handleWriteFile(ctx, args) },
  { name: 'editFile', schema: editFileToolSchema, handler: (ctx, args) => handleEditFile(ctx, args) },
  { name: 'copyFile', schema: copyFileToolSchema, handler: (ctx, args) => handleCopyFile(ctx, args) },
  { name: 'rgSearch', schema: rgSearchToolSchema, handler: (ctx, args) => handleRgSearch(ctx, args) },
  { name: 'writeTodos', schema: writeTodosToolSchema, handler: (ctx, args) => handleWriteTodos(ctx, args) },
  { name: 'readTodos', schema: readTodosToolSchema, handler: (ctx) => handleReadTodos(ctx) },
  { name: 'updateTodoStatus', schema: updateTodoStatusToolSchema, handler: (ctx, args) => handleUpdateTodoStatus(ctx, args) },
  { name: 'libraryList', schema: libraryListToolSchema, handler: (ctx) => handleLibraryList(ctx) },
  { name: 'libraryDocs', schema: libraryDocsToolSchema, handler: (ctx, args) => handleLibraryDocs(ctx, args) },
  { name: 'diff', schema: diffToolSchema, handler: (ctx, args) => handleDiff(ctx, args) },
];

// Tool groupings (PLAN vs CODING) mirroring Agent behavior
const PLAN_NAMES = [
  'listFiles',
  'readFile',
  'rgSearch',
  'writeTodos',
  'readTodos',
  'updateTodoStatus',
  'libraryList',
  'libraryDocs',
  'diff',
];

export const ALL_TOOL_NAMES = [
  'listFiles',
  'readFile',
  'createFile',
  'writeFile',
  'editFile',
  'copyFile',
  'rgSearch',
  'writeTodos',
  'readTodos',
  'updateTodoStatus',
  'libraryList',
  'libraryDocs',
  'diff',
];

const CODING_NAMES = [
  ...ALL_TOOL_NAMES
];

export class Tools {

  static getSchemas(names: string[]): any[] {
    const schemasByName = new Map(TOOL_DEFS.map(d => [d.name, d.schema] as const));
    return names.map(n => schemasByName.get(n)).filter(Boolean) as any[];
  }

  // Dispatch tool call to registered handler
  static async dispatch(ctx: ToolContext, toolCall: ToolCall): Promise<{ tool_call_id: string; content: string }> {
    const name = toolCall.function?.name;
    const args = toolCall.function?.arguments || {};
    const tool = TOOL_DEFS.find(t => t.name === name);
    if (!tool) {
      return { tool_call_id: toolCall.id, content: `Error: Unknown tool: ${name}` };
    }
    try {
      const out = await tool.handler(ctx, args);
      return { tool_call_id: toolCall.id, content: typeof out === 'string' ? out : String(out) };
    } catch (e: any) {
      const msg = (e?.message || String(e)).toString();
      const safeArgs = JSON.stringify(args ?? {}).slice(0, 400);
      return { tool_call_id: toolCall.id, content: `Error: ${msg}. Tool: ${name}. Args: ${safeArgs}` };
    }
  }
}

export default Tools;
